import {
  ProposalProduct,
  ProposalProductGroup,
  ProposalStage,
  UpdateProposalProductsOrderingInput,
} from "generated/graphql";

export type GroupState = {
  id: string | null;
  name: string;
  isOptional: boolean;
  showIndividualProducts: boolean;
  products: ProposalProduct[];
};

export type StageItem =
  | { kind: "product"; product: ProposalProduct }
  | { kind: "group"; group: GroupState };

export type StageOrderingState = {
  stageId: string;
  items: StageItem[];
};

export function buildStageState(stage: ProposalStage): StageOrderingState {
  const productById = new Map((stage.proposalProducts ?? []).map((p) => [p.id, p]));
  const groupedProductIds = new Set<string>();

  const groups = (stage.proposalProductGroups ?? []).map((g) => {
    const groupProducts = (g.proposalProducts ?? [])
      .map((ref) => {
        groupedProductIds.add(ref.id);
        return productById.get(ref.id);
      })
      .filter((p): p is ProposalProduct => p != null)
      .sort((a, b) => (a.ordering ?? 0) - (b.ordering ?? 0));

    return {
      kind: "group" as const,
      _ord: g.ordering,
      group: {
        id: g.id,
        name: g.name,
        isOptional: g.isOptional,
        showIndividualProducts: g.showIndividualProducts,
        products: groupProducts,
      },
    };
  });

  const ungrouped = (stage.proposalProducts ?? [])
    .filter((p) => !groupedProductIds.has(p.id))
    .map((p) => ({
      kind: "product" as const,
      _ord: p.ordering,
      product: p,
    }));

  const items = [...ungrouped, ...groups]
    .sort((a, b) => a._ord - b._ord)
    .map(({ _ord, ...rest }) => rest as StageItem);

  return { stageId: stage.id, items };
}

export function buildMutationInput(state: StageOrderingState): UpdateProposalProductsOrderingInput {
  const groups: any[] = [];
  const productUpdates: any[] = [];

  state.items.forEach((item, stageIndex) => {
    if (item.kind === "group") {
      const { group } = item;
      groups.push({
        ...(group.id ? { id: group.id } : {}),
        name: group.name,
        ordering: stageIndex,
        isOptional: group.isOptional,
        showIndividualProducts: group.showIndividualProducts ?? false,
        proposalProducts: {
          connect: group.products.map((p) => p.id),
        },
      });
      group.products.forEach((p, groupIndex) => {
        productUpdates.push({ id: p.id, ordering: groupIndex });
      });
    } else {
      productUpdates.push({
        id: item.product.id,
        ordering: stageIndex,
        proposalProductGroupId: null,
      });
    }
  });

  return {
    id: state.stageId,
    proposalProductGroups: { upsert: groups },
    proposalProducts: { update: productUpdates },
  };
}

export function getProductDisplayName(product: ProposalProduct): string {
  return (
    product.externalProductName || product.material?.externalProductName || product.material?.name
  );
}

export function groupAggregates(products: ProposalProduct[]) {
  const sum = (field: keyof ProposalProduct) =>
    products.reduce((acc, p) => acc + Number((p as any)[field] ?? 0), 0);
  return {
    costTotal: sum("costTotal"),
    profit: products.reduce(
      (acc, p) => acc + Number(p.profitAfterDiscount ?? p.profitOverride ?? p.profit ?? 0),
      0
    ),
    discount: sum("discount"),
    overallTotal: products.reduce(
      (acc, p) =>
        acc + Number(p.overallTotalAfterDiscount ?? p.overallTotalOverride ?? p.overallTotal ?? 0),
      0
    ),
  };
}

export function validateStageState(state: StageOrderingState): string | null {
  for (const item of state.items) {
    if (item.kind === "group") {
      if (!item.group.name.trim()) {
        return "Group name is required.";
      }
      if (item.group.products.length < 2) {
        return "A group must contain at least 2 products.";
      }
    }
  }
  return null;
}
